import {useState, useEffect} from 'react'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import useTags from '../../../hooks/useTags'
import useUser from '../../../hooks/useUser'
import {Input, SelectElem} from '../../../components/forms/inputs'
import {modalities as allModalities, taskTypes} from '../../../components/filters/constants'
import {CheckboxGroup, OptionsGroup, SingleRangeSlider} from '../../../components/filters/widgets'
import {Evaluation as EvaluationType} from '../../../services/types'
import {PRIVATE_EVALUATION} from '../../../permissions'
import type {EvaluationStageProps} from './types'

const PUBLIC_VISIBLE = 'publicVisible'

type EvaluationSettingsProps = {
  evaluation: EvaluationType
  onChange: (field: keyof EvaluationType, val: any) => void
  validate?: boolean
}
export const EvaluationSettings = ({evaluation, onChange, validate}: EvaluationSettingsProps) => {
  const {hasPermission} = useUser()
  const [types, setTypes] = useState(evaluation?.task_types || [])
  const [modalities, setModalities] = useState<string[]>(evaluation?.modalities || [])
  const [isPublic, setPublic] = useState(evaluation?.public || false)
  const [selectedTags, setSelectedTags] = useState(evaluation?.tags?.map(({id}) => id) || [])
  const [minQuestions, setMinQuestions] = useState<number>(
    evaluation?.min_questions_to_complete || 20
  )
  const {tags} = useTags()

  const setVal = (field: keyof EvaluationType, updater: (v: any) => void) => (val: any) => {
    updater(val)
    onChange(field, val)
  }
  const updateTags = (tagIds: string[]) => {
    setSelectedTags(tagIds)
    onChange(
      'tags',
      tags.filter(({id}) => tagIds.includes(id))
    )
  }

  return (
    <Grid container>
      <Grid item xs={12} sm={6} md={3}>
        <CheckboxGroup
          label="Type"
          allOptions={taskTypes}
          selected={types}
          onChange={setVal('task_types', setTypes)}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <CheckboxGroup
          label="Modalities"
          allOptions={allModalities}
          selected={modalities}
          onChange={setVal('modalities', setModalities)}
        />
      </Grid>

      {hasPermission(PRIVATE_EVALUATION) ? (
        <Grid item xs={12} sm={6} md={3}>
          <CheckboxGroup
            label="Visibility"
            allOptions={{
              [PUBLIC_VISIBLE]: 'Visible to everyone',
            }}
            info={{
              [PUBLIC_VISIBLE]:
                'When set, this evaluation can be viewed by anyone, including running it on models.',
            }}
            selected={isPublic ? [PUBLIC_VISIBLE] : undefined}
            onChange={(vals: string[]) =>
              setVal('public', setPublic)(vals.includes(PUBLIC_VISIBLE))
            }
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={6} md={3}>
          {' '}
        </Grid>
      )}

      <Grid item xs={12} sm={6} md={3}>
        <OptionsGroup label="Tags">
          <SelectElem
            label="Tags"
            multiple
            onChange={updateTags}
            value={selectedTags}
            values={tags.reduce((acc, {id, name}) => ({...acc, [id]: name}), {})}
            sx={{maxWidth: 300}}
          />
        </OptionsGroup>
      </Grid>

      <Grid item xs={12} sm={6}>
        <SingleRangeSlider
          label="Mininum questions to complete"
          val={minQuestions}
          min={20}
          max={Math.max(10000)}
          onChange={setVal('min_questions_to_complete', setMinQuestions)}
        />
      </Grid>
    </Grid>
  )
}

const BasicSettings = ({evaluation, onChange, setNextHandler, index}: EvaluationStageProps) => {
  const [validate, setValidate] = useState(false)

  const checkCorrect = () => {
    const valid = !!(evaluation.name && evaluation.description)
    setValidate(!valid)
    return valid
  }

  useEffect(() => {
    setNextHandler(checkCorrect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, evaluation])

  const setVal = (field: keyof EvaluationType) => (val: string) => onChange(field, val)

  return (
    <Stack spacing={2}>
      <Input
        label="Name"
        defaultValue={evaluation.name}
        setter={setVal('name')}
        validate={validate}
        validator={(v) => !v && 'Name is required'}
        sx={{width: '100%'}}
      />
      <Input
        label="Description"
        defaultValue={evaluation.description}
        validate={validate}
        setter={setVal('description')}
        validator={(v) => !v && 'Please provide a description'}
        multiline
        sx={{width: '100%'}}
      />
      <EvaluationSettings evaluation={evaluation} onChange={onChange} />
    </Stack>
  )
}

export default BasicSettings
